import { useState, useEffect } from "react";
import { listProfiles, createProfile, updateProfile, deleteProfile, analyzeText } from "../lib/api";
import TextRenderer from "../components/TextRenderer";
import { Settings, Plus, Trash2, Check, Palette, Type, EyeOff, StretchHorizontal, Eye, ArrowLeft } from "lucide-react";

const SAMPLE = "Le petit chat dort sur le tapis. La souris mange du fromage dans la cuisine.";

const GRAPHEME_OPTIONS = [
  "ou", "on", "an", "en", "ch", "oi", "in", "eau", "au", "ai", "ph", "gn", "ei", "un",
];

export default function ProfileManagerPage() {
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mobile: show detail panel
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => { loadProfiles(); }, []);

  const loadProfiles = async () => {
    try {
      const res = await listProfiles();
      setProfiles(res.data);
      if (res.data.length > 0 && !selected) {
        setSelected(res.data[0].id);
        previewProfile(res.data[0]);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const previewProfile = async (profile) => {
    try {
      const res = await analyzeText(SAMPLE, profile.id);
      setAnalysis(res.data);
    } catch (err) { console.error(err); }
  };

  const selectProfile = (profile) => {
    setSelected(profile.id);
    setEditing(null);
    previewProfile(profile);
    setShowDetail(true);
  };

  const startEditing = (profile) => {
    if (profile.is_system) return;
    setEditing({ ...profile, rules: { ...profile.rules } });
  };

  const handleCreateNew = async () => {
    try {
      const res = await createProfile({
        name: "Nouveau profil",
        description: "Mon profil personnalise",
        rules: {
          syllable_coloring: { enabled: true, colors: ["#1D4ED8", "#C2410C"] },
          silent_letters: { enabled: false, style: "opacity" },
          line_alternation: { enabled: false, colors: ["#EFF6FF", "#FFF7ED"] },
          grapheme_highlight: { enabled: false, graphemes: [] },
        },
      });
      await loadProfiles();
      setSelected(res.data.id);
      startEditing(res.data);
      setShowDetail(true);
    } catch (err) { console.error(err); }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      await updateProfile(editing.id, { name: editing.name, description: editing.description, rules: editing.rules });
      setEditing(null);
      await loadProfiles();
      const res = await analyzeText(SAMPLE, editing.id);
      setAnalysis(res.data);
    } catch (err) { console.error(err); }
  };

  const handleDeleteProfile = async (id) => {
    try {
      await deleteProfile(id);
      setShowDetail(false);
      await loadProfiles();
    } catch (err) { console.error(err); }
  };

  const toggleRule = (ruleKey) => {
    if (!editing) return;
    setEditing((prev) => ({
      ...prev,
      rules: { ...prev.rules, [ruleKey]: { ...prev.rules[ruleKey], enabled: !prev.rules[ruleKey]?.enabled } },
    }));
  };

  const toggleGrapheme = (grapheme) => {
    if (!editing) return;
    setEditing((prev) => {
      const current = prev.rules.grapheme_highlight?.graphemes || [];
      const updated = current.includes(grapheme) ? current.filter((g) => g !== grapheme) : [...current, grapheme];
      return { ...prev, rules: { ...prev.rules, grapheme_highlight: { ...prev.rules.grapheme_highlight, graphemes: updated } } };
    });
  };

  const currentProfile = profiles.find((p) => p.id === selected);

  return (
    <div
      data-testid="profiles-page"
      className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 page-enter has-bottom-nav lg:pb-8"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6 md:mb-8">
        {/* Mobile: back button when in detail view */}
        {showDetail && (
          <button
            data-testid="profiles-back-btn"
            onClick={() => setShowDetail(false)}
            className="lg:hidden p-2 rounded-xl text-slate-400 active:text-slate-700 active:bg-slate-100 transition-all touch-target mr-2"
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <h1
          data-testid="profiles-title"
          className="font-fredoka text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800 flex items-center gap-2 flex-1"
        >
          <Settings size={24} className="text-blue-500 hidden md:block" />
          {showDetail ? (currentProfile?.name || "Profil") : "Profils"}
        </h1>
        <button
          data-testid="create-profile-btn"
          onClick={handleCreateNew}
          className="flex items-center gap-1.5 bg-orange-500 active:bg-orange-600 text-white font-fredoka font-bold py-2.5 px-4 md:py-3 md:px-6 rounded-full shadow-[0_4px_0_rgb(194,65,12)] btn-bouncy transition-all touch-target text-sm md:text-base"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Creer un profil</span>
          <span className="sm:hidden">Creer</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Profile list - hidden on mobile when detail is shown */}
        <div className={`space-y-2 md:space-y-3 ${showDetail ? "hidden lg:block" : "block"}`}>
          {profiles.map((p) => (
            <div
              key={p.id}
              data-testid={`profile-card-${p.id}`}
              onClick={() => selectProfile(p)}
              className={`bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 group touch-target ${
                selected === p.id ? "border-orange-400 shadow-md" : "border-slate-100 active:border-orange-200"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-fredoka font-bold text-slate-800 text-base">{p.name}</h3>
                  <p className="font-nunito text-xs text-slate-400 mt-0.5">{p.description}</p>
                  {p.is_system && (
                    <span className="inline-block mt-1 text-[10px] font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full uppercase">
                      Systeme
                    </span>
                  )}
                </div>
                {!p.is_system && (
                  <button
                    data-testid={`delete-profile-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); handleDeleteProfile(p.id); }}
                    className="p-2 rounded-lg text-slate-300 active:text-red-500 active:bg-red-50 transition-all lg:opacity-0 lg:group-hover:opacity-100 touch-target"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                {p.rules?.syllable_coloring?.enabled && (
                  <span className="w-7 h-7 md:w-6 md:h-6 rounded-full bg-blue-100 flex items-center justify-center">
                    <Palette size={12} className="text-blue-600" />
                  </span>
                )}
                {p.rules?.silent_letters?.enabled && (
                  <span className="w-7 h-7 md:w-6 md:h-6 rounded-full bg-slate-100 flex items-center justify-center">
                    <EyeOff size={12} className="text-slate-500" />
                  </span>
                )}
                {p.rules?.line_alternation?.enabled && (
                  <span className="w-7 h-7 md:w-6 md:h-6 rounded-full bg-orange-100 flex items-center justify-center">
                    <StretchHorizontal size={12} className="text-orange-500" />
                  </span>
                )}
                {p.rules?.grapheme_highlight?.enabled && (
                  <span className="w-7 h-7 md:w-6 md:h-6 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Type size={12} className="text-yellow-600" />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Detail panel - full width on mobile, shown conditionally */}
        <div className={`lg:col-span-2 space-y-6 ${showDetail ? "block" : "hidden lg:block"}`}>
          {/* Config panel */}
          <div className="bg-white rounded-2xl md:rounded-3xl border-2 border-slate-100 p-5 md:p-6 shadow-md">
            {currentProfile && !editing && (
              <div>
                <h2 className="font-fredoka text-lg md:text-xl font-bold text-slate-800 mb-2">
                  {currentProfile.name}
                </h2>
                <p className="font-nunito text-sm text-slate-500 mb-5">{currentProfile.description}</p>
                <div className="space-y-2 md:space-y-3">
                  <RuleDisplay label="Syllabes colorees" icon={<Palette size={16} className="text-blue-500" />} enabled={currentProfile.rules?.syllable_coloring?.enabled} />
                  <RuleDisplay label="Lettres muettes" icon={<EyeOff size={16} className="text-slate-500" />} enabled={currentProfile.rules?.silent_letters?.enabled} />
                  <RuleDisplay label="Alternance de lignes" icon={<StretchHorizontal size={16} className="text-orange-500" />} enabled={currentProfile.rules?.line_alternation?.enabled} />
                  <RuleDisplay label="Graphemes complexes" icon={<Type size={16} className="text-yellow-600" />} enabled={currentProfile.rules?.grapheme_highlight?.enabled} />
                </div>
                {!currentProfile.is_system && (
                  <button
                    data-testid="edit-profile-btn"
                    onClick={() => startEditing(currentProfile)}
                    className="mt-5 w-full py-3 md:py-3.5 bg-blue-500 active:bg-blue-600 text-white font-fredoka font-bold rounded-xl shadow-[0_4px_0_rgb(29,78,216)] btn-bouncy transition-all touch-target"
                  >
                    Modifier ce profil
                  </button>
                )}
              </div>
            )}

            {editing && (
              <div className="space-y-4">
                <input
                  data-testid="edit-profile-name"
                  value={editing.name}
                  onChange={(e) => setEditing((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full font-fredoka text-lg md:text-xl font-bold text-slate-800 bg-transparent border-b-2 border-slate-200 focus:border-orange-400 outline-none pb-1"
                />
                <div className="space-y-2 md:space-y-3 mt-4">
                  <RuleToggle label="Syllabes colorees" icon={<Palette size={16} className="text-blue-500" />} enabled={editing.rules?.syllable_coloring?.enabled} onToggle={() => toggleRule("syllable_coloring")} />
                  <RuleToggle label="Lettres muettes" icon={<EyeOff size={16} className="text-slate-500" />} enabled={editing.rules?.silent_letters?.enabled} onToggle={() => toggleRule("silent_letters")} />
                  <RuleToggle label="Alternance de lignes" icon={<StretchHorizontal size={16} className="text-orange-500" />} enabled={editing.rules?.line_alternation?.enabled} onToggle={() => toggleRule("line_alternation")} />
                  <RuleToggle label="Graphemes complexes" icon={<Type size={16} className="text-yellow-600" />} enabled={editing.rules?.grapheme_highlight?.enabled} onToggle={() => toggleRule("grapheme_highlight")} />
                </div>

                {editing.rules?.grapheme_highlight?.enabled && (
                  <div className="mt-4">
                    <p className="font-nunito font-bold text-xs text-slate-500 uppercase tracking-wider mb-2">Graphemes a surligner</p>
                    <div className="flex flex-wrap gap-2">
                      {GRAPHEME_OPTIONS.map((g) => (
                        <button
                          key={g}
                          data-testid={`grapheme-toggle-${g}`}
                          onClick={() => toggleGrapheme(g)}
                          className={`px-3 py-2 rounded-lg font-nunito font-bold text-sm transition-all touch-target ${
                            editing.rules.grapheme_highlight.graphemes?.includes(g)
                              ? "bg-yellow-400 text-slate-800"
                              : "bg-slate-100 text-slate-500 active:bg-slate-200"
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <button
                    data-testid="save-profile-btn"
                    onClick={handleSaveEdit}
                    className="flex-1 py-3 md:py-3.5 bg-orange-500 active:bg-orange-600 text-white font-fredoka font-bold rounded-xl shadow-[0_4px_0_rgb(194,65,12)] btn-bouncy transition-all flex items-center justify-center gap-2 touch-target"
                  >
                    <Check size={18} />
                    Enregistrer
                  </button>
                  <button
                    data-testid="cancel-edit-btn"
                    onClick={() => setEditing(null)}
                    className="py-3 px-4 md:px-5 bg-slate-100 text-slate-600 font-fredoka font-bold rounded-xl active:bg-slate-200 transition-all touch-target"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="bg-white rounded-2xl md:rounded-3xl border-2 border-slate-100 p-5 md:p-6 shadow-md">
            <h2 className="font-fredoka text-base md:text-lg font-bold text-slate-700 mb-3 md:mb-4 flex items-center gap-2">
              <Eye size={18} className="text-orange-500" />
              Apercu
            </h2>
            <div className="bg-orange-50/50 rounded-2xl p-3 md:p-4 min-h-[150px] md:min-h-[200px]">
              {analysis ? (
                <TextRenderer analysis={analysis.analysis} profile={analysis.profile} />
              ) : (
                <p className="text-slate-400 font-lexend italic">Chargement...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RuleDisplay({ label, icon, enabled }) {
  return (
    <div className="flex items-center justify-between p-3 md:p-3.5 bg-slate-50 rounded-xl touch-target">
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-nunito font-bold text-sm text-slate-700">{label}</span>
      </div>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${enabled ? "bg-green-100 text-green-600" : "bg-slate-200 text-slate-400"}`}>
        {enabled ? "Actif" : "Inactif"}
      </span>
    </div>
  );
}

function RuleToggle({ label, icon, enabled, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between p-3 md:p-3.5 rounded-xl transition-all touch-target ${
        enabled ? "bg-green-50 border-2 border-green-200" : "bg-slate-50 border-2 border-transparent"
      }`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-nunito font-bold text-sm text-slate-700">{label}</span>
      </div>
      <div className={`w-12 h-7 rounded-full transition-all flex items-center ${enabled ? "bg-green-500 justify-end" : "bg-slate-300 justify-start"}`}>
        <div className="w-5 h-5 bg-white rounded-full mx-1 shadow-sm" />
      </div>
    </button>
  );
}
